import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';

/**
 * 마이페이지 복지·인사 (DOC-47 §3.1 · §3.3 · §3.4 · §3.5 · §3.6).
 *
 * 셋으로 나눠 읽는다 — 복지(검진·교육·경조/장학) · 숙소/귀향 · 인사(선임·승진·수당).
 * 한 덩어리로 묶지 않은 이유는 **숙소/귀향만 외부 호출(라우팅)을 타기 때문**이다. 그 탭을 열지
 * 않는 사람에게까지 OSRM 을 부르면, 아무도 안 보는 값을 위해 매번 몇 초를 기다린다.
 */

export type CheckupTarget = {
  standardId: string;
  name: string;
  checkupType: string;
  cycleMonths: number | null;
  lastDate: string | null;
  dueOn: string | null;
  overdue: boolean;
  eligible: boolean;
  reason: string | null;
};

export type EducationTarget = {
  standardId: string;
  name: string;
  legalBasis: string | null;
  cycleMonths: number;
  hours: number | null;
  completedOn: string | null;
  dueOn: string | null;
  done: boolean;
};

export type BenefitOption = {
  standardId: string;
  kind: 'CONGRATULATION' | 'SCHOLARSHIP';
  name: string;
  subject: string | null;
  /** 지급률을 가르는 성적 조건(「4.0 이상」·「F, 학사경고」). 경조에는 없다. */
  condition: string | null;
  amount: number | null;
  /**
   * 지급률(%). <b>금액이 비어 있고 이 값이 있으면</b> 신청액(등록금)에 곱해서 지급액이 된다 —
   * 장학금은 학기마다 금액이 다르다. 0 은 「대상 아님」 이다(F·학사경고 → 제외).
   */
  rate: number | null;
  leaveDays: number | null;
  eligible: boolean;
  reason: string | null;
};

export type BenefitClaim = {
  id: string;
  claimType: string;
  subject: string | null;
  eventDate: string | null;
  amount: number | null;
  leaveDays: number | null;
  status: string;
};

export type MyWelfare = {
  checkups: CheckupTarget[];
  educations: EducationTarget[];
  benefitOptions: BenefitOption[];
  claims: BenefitClaim[];
};

export type Eligibility = {
  tier: 'MANDATORY' | 'REVIEW' | 'NONE';
  tierLabel: string;
  reason: string | null;
  /** 판정이 문턱 ±5km 안이다. 거주지 좌표가 구 중심이라 자동 확정하지 않는다. */
  borderline: boolean;
  residenceLabel: string | null;
  siteName: string | null;
  siteAddress: string | null;
  /** 판정에 실제로 쓴 거리. 기본은 도로거리다. */
  distanceKm: number | null;
  /** KAKAO(3경로 평균 도로거리) · OSRM · ORS · STRAIGHT(라우팅이 막혀 직선으로 물러섬) */
  distanceSource: string | null;
  /** 참고용 직선거리. 지도에서 재 본 값과 다를 때 어느 자의 차이인지 보이라고 함께 낸다. */
  straightLineKm: number | null;
  travelMinutes: number | null;
  travelSource: string | null;
  roundTripAmount: number | null;
  monthlyRoundTrips: number | null;
  monthlyCap: number | null;
  /** 거주지 **구 중심** 좌표. 집 위치가 아니라 「강서구」 라고 적는 것과 같은 정보량이다. */
  residenceLat: number | null;
  residenceLng: number | null;
  siteLat: number | null;
  siteLng: number | null;
  /** 발령일 — 숙소 입주 예정일의 기본값. 무기한 배치면 종료일은 비어 온다. */
  assignmentStartDate: string | null;
  assignmentEndDate: string | null;
  /**
   * 제주·도서 등 **특별지역** 현장인가.
   *
   * 참이면 `monthlyCap` 이 0 이다 — **못 받는다는 뜻이 아니라** 구간표로 계산하지 않는다는
   * 뜻이다. 0 만 보여 주면 대상이 아닌 것으로 읽히므로 화면이 「별도 지급」 이라고 적어야 한다.
   */
  specialArea: boolean;
};

export type MyRelocation = {
  residence: { sido: string; sigungu: string; label: string; geocodeStatus: string } | null;
  eligibility: Eligibility | null;
  lodgingApplications: Array<{
    id: string; siteName: string; status: string; tier: string;
    distanceKm: number | null; travelMinutes: number | null;
    checkInDate: string | null; roomLabel: string | null; rejectReason: string | null;
  }>;
  homecomingApplications: Array<{
    id: string; siteName: string; status: string; tier: string;
    distanceKm: number | null; travelMinutes: number | null;
    monthlyCap: number | null; rejectReason: string | null;
    /** 특별지역이면 `monthlyCap` 은 인사팀이 승인하면서 적은 금액이다(V562). 옛 서버는 안 보낸다. */
    roundTripAmount?: number | null; monthlyRoundTrips?: number | null; specialArea?: boolean;
  }>;
  claims: Array<{
    id: string; claimMonth: string; claimedAmount: number; capAmount: number;
    payableAmount: number; status: string;
  }>;
};

export type MyCareer = {
  duties: Array<{
    id: string; dutyName: string; placeName: string | null; appointedOn: string;
    reported: boolean; licenseName: string | null; licenseExpiry: string | null; licenseExpired: boolean;
  }>;
  licenses: Array<{
    id: string; name: string; issuer: string | null; acquiredDate: string | null;
    expiryDate: string | null; expired: boolean; daysUntilExpiry: number | null;
  }>;
  promotion: {
    /** 승진 사다리의 꼭대기(부장). 화면은 「승진 요건」 칸을 아예 내지 않는다. */
    atTop: boolean;
    available: boolean;
    reason: string | null;
    fromRank: string | null;
    toRank: string | null;
    requiredRankMonths: number | null;
    currentRankMonths: number | null;
    /** 직급 이력이 없어 입사일로 대신 셌다. 화면이 그 사실을 적는다. */
    rankMonthsAssumed: boolean;
    rankMonthsMetOn: string | null;
    /** 적용된 기준 줄의 학력. 빈 문자열이면 「학력 무관」 줄로 판정했다는 뜻이다(V93). */
    appliedEducation: string | null;
    /** 현 직급에서 쌓은 고과 점수의 합. 승진하면 0에서 다시 쌓는다. C·D 는 음수다. */
    requiredEvaluationScore: number | null;
    currentEvaluationScore: number | null;
    evaluationCounted: number;
    /** 환산표에 없는 등급이라 **세지 못한** 개수. 0 이 아니면 합계가 실제보다 낮다. */
    evaluationUnmatched: number;
    /** 고과 환산표가 비어 있다. 합계 0 은 「나쁘다」가 아니라 「기준 미등록」이다. */
    evaluationTableMissing: boolean;
    evaluationGrades: string[];
    licenseUnmatched: number;
    licenseTableMissing: boolean;
    requiredLicenseScore: number | null;
    currentLicenseScore: number | null;
    rankOk: boolean;
    evaluationOk: boolean;
    licenseOk: boolean;
    /** 승진한 해부터 전년까지의 햇수 — 근속 판정은 이 값으로 한다(V563). 재시작 전 서버는 안 보낸다. */
    currentRankYears?: number | null;
    /** 대상이 되는 연초 심사 연도. */
    reviewYear?: number | null;
  };
  allowances: Array<{
    kind: string;
    name: string;
    eligible: boolean;
    reason: string | null;
    /**
     * 이 줄의 <b>기준값을 인사팀이 확인했는가</b>(V178). 거짓이면 자격증 이름·근속 개월 수가
     * 우리가 세워 둔 예시라, 화면이 「확인 필요」를 붙여 판정을 참고값으로 읽게 한다.
     */
    confirmed: boolean;
    /**
     * 기준표의 금액(원, V567). 비면 금액 미정. 재시작 전 서버는 안 보낸다.
     * {@link payCycle} 이 YEAR_END 면 월 금액이 아니라 연말에 한 번 나가는 금액이다.
     */
    monthlyAmount?: number | null;
    /** MONTHLY 매월 · YEAR_END 연말 1회(V583). 재시작 전 서버는 안 보낸다 — 없으면 매월로 읽는다. */
    payCycle?: 'MONTHLY' | 'YEAR_END' | null;
    /** 이번 달 지급액 — 발령일 기준 일할(2026-10-28). 재시작 전 서버는 안 보낸다. */
    monthAmount?: number | null;
    /** 이번 달 지급일수 · 그 달 일수. 둘이 다르면 일할된 달이다. */
    paidDays?: number | null;
    monthDays?: number | null;
    /** 근속 조건이 채워진 날. 있으면 근속수당 신청 대상이다(근속수당 알림). */
    tenureReachedOn?: string | null;
  }>;
  rankHistories: Array<{ rankName: string; effectiveDate: string; reason: string | null }>;
  /**
   * 근무처이력 (V349 · 사용자 지시 2026-09-29) — <b>언제부터 언제까지 어디에</b>.
   *
   * 인사팀이 인사자료 관리에서 적은 줄을 그대로 읽는다(본인이 고치는 자료가 아니다). 진행 중인
   * 근무가 맨 위이고, `endDate` 가 비어 있으면 <b>지금 그 자리</b>다 — 화면이 「~현재」로 적는다.
   */
  worksites: Array<{
    placeType: 'site' | 'department';
    placeName: string;
    roleTitle: string | null;
    startDate: string;
    endDate: string | null;
    note: string | null;
  }>;
};

/** 서버 `PersonalAssetService.CATEGORIES` 와 <b>같은 낱말</b>이어야 한다. */
export const ASSET_CATEGORIES = [
  { value: 'PC', label: 'PC · 노트북' },
  { value: 'MONITOR', label: '모니터' },
  { value: 'PHONE', label: '휴대폰' },
  { value: 'SOFTWARE', label: '소프트웨어' },
  { value: 'ETC', label: '기타' },
] as const;

export type MyAsset = {
  id: string;
  /**
   * 이 줄을 적은 쪽 (V226). <b>`HR` 이면 화면에서 못 고친다</b> — 인사총무팀이 등록한 지급 기록이고,
   * 받은 쪽이 고쳐 버리면 그 줄은 더 이상 대장이 아니다. 서버도 같은 규칙으로 막는다.
   */
  source: 'SELF' | 'HR';
  category: string;
  name: string;
  spec: string | null;
  serialNo: string | null;
  issuedOn: string | null;
  /** 차 있으면 지금 쓰는 것이 아니다 — 목록에서 아래로 내려간다. */
  returnedOn: string | null;
  note: string | null;
};

/**
 * 총무의 배포 대장(V74)이 아는 내 라이선스. <b>화면에서 고칠 수 없다.</b>
 *
 * <p>신청 탭에서 <b>불출</b>되면(V265) 배포 행에 내 `personnel_id` 가 박히므로 이 목록에 그대로
 * 올라온다 — 마이페이지가 따로 적는 값이 아니다. 회수되면 같은 이유로 사라진다.
 */
export type MySoftwareSeat = {
  seatId: string;
  productName: string;
  edition: string | null;
  vendorName: string | null;
  licenseType: string;
  deviceName: string | null;
  assignedOn: string | null;
  expiresOn: string | null;
  /** 만료 여부는 <b>서버가 답한다</b> — 브라우저 시계로 재면 사람마다 다른 답이 나온다. */
  expired: boolean;
  /** 라이선스 키가 등록돼 있는가. <b>값은 실려 오지 않는다</b> — 비밀번호를 넣어야 열린다. */
  hasKey: boolean;
  note: string | null;
};

export type MyAssets = { assets: MyAsset[]; software: MySoftwareSeat[] };

export type AssetWrite = {
  category: string;
  name: string;
  spec: string | null;
  serialNo: string | null;
  issuedOn: string | null;
  returnedOn: string | null;
  note: string | null;
};

/**
 * 생일 한 값. <b>복지몰 포인트는 없다</b> (사용자 지시 2026-09-03: 「복지몰은 그냥 링크같은거만
 * 넣자. 포인트를 연동할 순 없을 것 같은데」) — 잔액은 복지몰이 갖고 있고 받아 올 길이 없다.
 * 복지몰 주소는 `useExternalLinkUrls()` 가 준다.
 */
export type MyBirthday = { birthDate: string | null };

function useMine<T>(key: string, path: string): UseQueryResult<T> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: [key, userId],
    queryFn: () => apiFetch<T>(path),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useMyWelfare() {
  return useMine<MyWelfare>('mypage-welfare', '/v1/mypage/welfare');
}

export function useMyCareer() {
  return useMine<MyCareer>('mypage-career', '/v1/mypage/career');
}

/**
 * 개인자산 — <b>내가 적은 것</b>과 <b>좌석 대장이 아는 것</b> (사용자 지시 2026-09-03).
 *
 * 한 요청으로 받는 이유는 화면이 둘을 같은 자리에서 보여 주기 때문이다. 나눠 부르면 소프트웨어
 * 칸만 늦게 채워져, 「내 캐드가 안 잡혔다」 로 읽히는 순간이 생긴다.
 */
export function useMyAssets() {
  return useMine<MyAssets>('mypage-assets', '/v1/mypage/assets');
}

/** 내 생일. 마이페이지의 다른 요청에는 실려 오지 않는 값이라 자리를 하나 낸다. */
export function useMyBirthday() {
  return useMine<MyBirthday>('mypage-birthday', '/v1/mypage/birthday');
}

/**
 * 자산 한 줄 넣기·고치기. <b>id 가 있으면 고치기</b>다 — 창이 하나라 보내는 곳도 하나다.
 *
 * <p>남의 줄은 서버가 막는다(`PersonalAssetService.mine`). 화면이 그 판단을 한 번 더 하지 않는
 * 이유는, 화면이 아는 목록이 곧 내 목록이라 <b>여기서 막을 것이 애초에 없기</b> 때문이다.
 */
export function useSaveAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AssetWrite & { id?: string }) =>
      apiFetch<MyAsset>(id ? `/v1/mypage/assets/${id}` : '/v1/mypage/assets', {
        method: id ? 'PUT' : 'POST',
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mypage-assets'] }),
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/mypage/assets/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mypage-assets'] }),
  });
}

/**
 * 내게(또는 내 현장에) 배포된 라이선스의 <b>키를 연다</b> — 본인 비밀번호를 넣는다(V266).
 *
 * <p>받은 값은 <b>캐시에 두지 않는다</b>(쿼리가 아니라 뮤테이션인 이유다) — 화면에 잠깐 보여 주고
 * 버린다. 남겨 두면 다른 탭을 다녀와도 키가 그대로 떠 있게 된다. 연 기록은 담당자 화면에 남는다.
 */
export function useRevealMySoftwareKey() {
  return useMutation({
    mutationFn: ({ seatId, password }: { seatId: string; password: string }) =>
      apiFetch<{
        productName: string;
        edition: string | null;
        licenseKey: string;
        /** 시리얼도 함께 온다 — 둘을 넣어야 설치된다(사용자 확인 2026-09-09). 없는 제품도 있다. */
        serialNo: string | null;
      }>(
        `/v1/mypage/software/${seatId}/key`,
        { method: 'POST', body: { password } },
      ),
  });
}

/**
 * 라우팅·지오코딩이 걸린 요청이라 **탭을 열 때만** 부른다.
 *
 * <h2>탭을 열 때마다 다시 묻는다 (2026-08-28)</h2>
 * 전에는 5분 동안 값을 재사용했다. 그런데 판정의 재료인 <b>배치는 이 화면 밖에서</b> 바뀐다
 * (인력배치표·인사발령). 남이 바꾼 값이라 무효화할 자리가 없고, 그 5분 동안 화면은 <b>옛 현장으로
 * 판정한 결과</b>를 자신 있게 보여 준다 — 「담당을 현장으로 바꿨는데 왜 그대로냐」 가 여기서 나온다.
 * 캐시가 가린 것이 「아직 계산 중」 이 아니라 <b>틀린 답</b>이라는 점이 문제다.
 *
 * <p>`enabled` 가 탭이 열린 동안에만 참이라, 값을 버려도 부르는 횟수는 <b>탭을 열 때마다 한 번</b>
 * 이다. 외부 호출이 붙은 요청이지만 사람이 이 탭을 하루에 몇 번 열지 않는다.
 */
export function useMyRelocation(enabled: boolean) {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['mypage-relocation', userId],
    queryFn: () => apiFetch<MyRelocation>('/v1/mypage/relocation'),
    enabled: enabled && !!userId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/** 숙소 탭에서 고를 수 있는 현장 — 주소가 있는 것만(서버가 거른다). */
export function useRelocationSites(enabled: boolean) {
  return useQuery({
    queryKey: ['mypage-relocation-sites'],
    queryFn: () => apiFetch<Array<{ id: string; name: string }>>('/v1/mypage/relocation/sites'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * 고른 현장까지의 판정 미리보기. 출발지는 등록된 거주지이고 저장하는 것은 없다.
 * 고를 때마다 길찾기를 한 번 부른다 — 같은 현장으로 돌아오면 캐시를 쓴다.
 */
export function useRelocationPreview(siteId: string | null) {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['mypage-relocation-preview', userId, siteId],
    queryFn: () => apiFetch<Eligibility>(`/v1/mypage/relocation/preview?siteId=${encodeURIComponent(siteId!)}`),
    enabled: !!siteId && !!userId,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useSaveResidence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { sido: string; sigungu: string }) =>
      apiFetch<MyRelocation['residence']>('/v1/mypage/relocation/residence', { method: 'PUT', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mypage-relocation'] });
      // 미리보기의 출발지도 거주지다 — 옛 거주지로 잰 값을 붙들고 있으면 안 된다.
      void queryClient.invalidateQueries({ queryKey: ['mypage-relocation-preview'] });
    },
  });
}

/**
 * 숙소 신청 — <b>결재를 거치지 않고 인사팀 요청으로 바로 간다</b>
 * (사용자 지시 2026-08-27 · DOC-68 §5.0).
 *
 * ## 기안 화면으로 보내지 않는다
 * 회사 숙소 배정은 자원 배분이지 지출 결정이 아니다. 방이 남았는지는 인사팀만 알고, 자격은
 * 거리로 이미 갈렸다 — 결재선에 선 사람이 신청자보다 아는 것이 적으면 그 결재는 도장이다.
 *
 * ## 날짜를 비우면 서버가 발령 기간으로 채운다
 * 그 값을 서버가 이미 알고 있다. 화면이 옮겨 적으면 거기서 틀리고, 그 오류는 인사팀이 방을
 * 배정한 뒤에야 드러난다. 그래서 <b>안 보낸 것</b>과 <b>빈 값으로 정한 것</b>을 가려야 한다.
 *
 * ## 거리·등급은 보내지 않는다
 * 서버가 지금 다시 판정해 넣는다. 화면에 떠 있던 판정이 낡았을 수 있고, 무엇보다 클라이언트가
 * 보낸 숫자를 대장에 굳히면 그 대장이 화면만큼만 정확해진다.
 */
export function useRequestLodging() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      checkInDate?: string | null;
      checkOutDate?: string | null;
      occupants?: number;
      roomPreference?: string | null;
      note?: string | null;
    } = {}) =>
      apiFetch<unknown>('/v1/mypage/relocation/lodging', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mypage-relocation'] });
      // 인사팀 목록에 그 줄이 바로 서야 한다 — 같은 화면을 두 사람이 보고 있다.
      void queryClient.invalidateQueries({ queryKey: ['hr-relocation-lodging'] });
    },
  });
}

/**
 * 귀향여비 대상 신청 — <b>결재 없이 인사팀으로</b>
 * (사용자 지시 2026-08-27: 「귀향여비도 결재 말고 바로 처리하게 하자」 · DOC-68 §5.0).
 *
 * ## 사람이 적을 것이 비고 하나다
 * 등급도 거리도 <b>월 한도</b>도 서버가 정한다 — 한도는 구간표가 낸 값이라 고를 여지가 없다.
 */
export function useRequestHomecoming() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { note?: string | null } = {}) =>
      apiFetch<unknown>('/v1/mypage/relocation/homecoming', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mypage-relocation'] });
      void queryClient.invalidateQueries({ queryKey: ['hr-relocation-homecoming'] });
    },
  });
}

/** 줄 하나 = 왕복 한 번. 서버가 월 왕복 횟수를 이 줄 수로 센다(V562). */
export type ClaimItemInput = {
  travelDate: string;
  route: string | null;
  transport: string | null;
  amount: number | null;
  /** 영수증 — `uploadClaimReceipt` 가 준 키와 파일 이름. 없으면 비운다. */
  evidenceKey?: string | null;
  evidenceName?: string | null;
};

/**
 * 영수증 한 장을 저장소에 올리고 키를 받는다. 파일 바이트는 서버를 지나지 않는다
 * (숙소 계약 첨부와 같은 presigned PUT 길).
 */
export async function uploadClaimReceipt(file: File): Promise<{ key: string; name: string }> {
  const ticket = await apiFetch<{ key: string; url: string }>('/v1/mypage/relocation/uploads', {
    method: 'POST',
    body: { fileName: file.name },
  });
  const response = await fetch(ticket.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!response.ok) throw new Error(`영수증을 저장소에 올리지 못했습니다. (HTTP ${response.status})`);
  return { key: ticket.key, name: file.name };
}

/**
 * 귀향여비 정산 — <b>결재 없이 인사팀으로.</b>
 *
 * ## 줄을 배열로 보낸다
 * 결재 서식을 거치던 때는 `2026-08-15|서울→평택|버스|24000` 을 줄바꿈으로 이어 붙였다. 서식의
 * 정형 항목이 평평한 키-값뿐이어서다. 그 제약이 사라졌으니 배열 그대로 보낸다 — 파이프로 이은
 * 줄은 한 칸이 비면 그 뒤가 통째로 밀린다.
 *
 * ## 지급액은 보내지 않는다
 * `min(실비, 한도)` 는 서버가 낸다. 화면이 계산해 보내면 한도가 바뀐 순간 두 값이 갈라진다.
 */
export function useRequestClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { claimMonth: string; items: ClaimItemInput[]; note?: string | null }) =>
      apiFetch<unknown>('/v1/mypage/relocation/homecoming-claims', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mypage-relocation'] });
      void queryClient.invalidateQueries({ queryKey: ['hr-relocation-claims'] });
    },
  });
}
